import { AutoAdjustBackwardsLimit } from "../../constants/autoAdjustBackwardsLimit";
import { AutoAdjustDateLevel } from "../../constants/autoAdjustDateLevel";
import compactDisplays from "../../constants/compactDisplay";
import defaultLegalNarrativeLabels from "../../constants/defaultLegalNarrativeLabels";
import itemTypeCategories from "../../constants/itemTypeCategories";
import itemTypeIds from "../../constants/itemTypeIds";
import narrativeNumberingOptions from "../../constants/narrativeNumberingOptions";
import { REFERENCE_RELATES_TO } from "../../constants/references";
import subwayDisplayOptions from "../../constants/subwayDisplayOptions";
import { TimelineZoomLimits } from "../../constants/zoomRange";
import { getGregorianCalendarObject } from "../../modules/definition/calendar/gregorianCalendarRaw";
import arrayToListObjectCamelCase from "../../utils/arrayToListObjectCamelCase";

const calendar = getGregorianCalendarObject();

export default {
  autoAdjustLevel: AutoAdjustDateLevel.AdjustSelf,
  autoAdjustBackwardsLimit: AutoAdjustBackwardsLimit.Today,
  includeNarrative: true,
  types: {
    byId: {
      testimony: {
        enabled: true,
        canDelete: true,
        id: "testimony",
        label: "Testimony",
        pluralLabel: "Testimony",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "scroll",
        color: "iconYellow",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "TST",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      party: {
        enabled: true,
        canDelete: true,
        id: "party",
        label: "Party",
        pluralLabel: "Parties",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "counselRef"],
        properties: ["notes", "roleInCase"],
        superTypes: [],
        icon: "people",
        color: "iconGreen",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "PA",
        compactDisplay: compactDisplays.AVATAR_INITIALS,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      [itemTypeIds.LOCATION_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.LOCATION_ITEM_TYPE_ID,
        label: "Location",
        pluralLabel: "Locations",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes", "shortName"],
        superTypes: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "map",
        color: "iconYellow",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "LOC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        category: itemTypeCategories.LOCATION,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      crossExamination: {
        enabled: true,
        canDelete: true,
        id: "crossExamination",
        label: "Cross examination",
        pluralLabel: "Cross examinations",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "comments",
        color: "iconOrange",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "CEX",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      directExamination: {
        enabled: true,
        canDelete: true,
        id: "directExamination",
        label: "Direct examination",
        pluralLabel: "Direct examinations",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "comment",
        color: "iconRed",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "DEX",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      [itemTypeIds.EVENT_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.EVENT_ITEM_TYPE_ID,
        label: "Event",
        pluralLabel: "Events",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: ["participantRef", "witnessRef", "locationRef", "evidenceRef", "documentRef", REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "fact"],
        icon: "diamond",
        color: "iconOrange",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "EV",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: true,
        relativeUnits: "seconds",
        defaultSequenceType: true,
        defaultNarrativeType: false
      },
      fact: {
        enabled: true,
        canDelete: true,
        id: "fact",
        label: "Fact",
        pluralLabel: "Facts",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: ["participantRef", "witnessRef", "locationRef", "evidenceRef", "documentRef", REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "fact"],
        icon: "checkedBox",
        color: "iconYellow",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "FCT",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: true,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      organisation: {
        enabled: true,
        canDelete: true,
        id: "organisation",
        label: "Organisation",
        pluralLabel: "Organisations",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          REFERENCE_RELATES_TO,
          "counselRef",
          "shareholderRef",
          "investorRef",
          "vendorRef",
          "serviceProviderRef",
          "subsidiaryRef"
        ],
        properties: ["notes", "shortName", "email", "phone", "address"],
        superTypes: ["organisation"],
        icon: "case",
        color: "iconOrange",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "ORG",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      [itemTypeIds.MINDMAP_NODE_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.MINDMAP_NODE_TYPE_ID,
        label: "Mindmap Node",
        pluralLabel: "Nodes",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "diamond",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NO",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      issue: {
        enabled: true,
        canDelete: true,
        id: "issue",
        label: "Issue",
        pluralLabel: "Issues",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "factRef", "keyFactRef"],
        properties: ["notes", "legal", "juryInstruction"],
        superTypes: [],
        icon: "flag",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "IS",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      [itemTypeIds.PERSON_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.PERSON_ITEM_TYPE_ID,
        label: "Person",
        pluralLabel: "People",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "familyRef",
          "friendRef",
          "associateRef",
          REFERENCE_RELATES_TO,
          "counselRef",
          "coworkerRef",
          "employerRef",
          "formerEmployerRef"
        ],
        properties: ["notes", "title", "shortName", "email", "phone", "address", "gender", "roleInCase"],
        superTypes: ["organisation", "party"],
        icon: "person",
        color: "iconRed",
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "PA",
        compactDisplay: compactDisplays.AVATAR_INITIALS,
        category: itemTypeCategories.PERSON,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      },
      [itemTypeIds.NARRATIVE_FOLDER_TYPE_ID]: {
        enabled: true,
        canDelete: false,
        id: itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
        label: "Narrative Folder",
        pluralLabel: "Narrative Folders",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "folder",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: true,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NAR",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false,
        isNarrativeFolder: true
      },
      source: {
        enabled: true,
        canDelete: true,
        id: "source",
        label: "Source",
        pluralLabel: "Sources",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          REFERENCE_RELATES_TO,
          "mentionsRef",
          "producedByRef",
          "authorRef",
          "contactRef",
          "sentFromRef",
          "sentToRef"
        ],
        properties: [
          "notes",
          "sourceType",
          "batesNoEnd",
          "batesNoBegin",
          "depositionExNo",
          "trailExNo",
          "status",
          "citation",
          "author",
          "relevance",
          "contact"
        ],
        superTypes: [],
        icon: "document",
        color: "iconPurple",
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "SRC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false
      }
    },
    allIds: [
      itemTypeIds.EVENT_ITEM_TYPE_ID,
      "fact",
      itemTypeIds.PERSON_ITEM_TYPE_ID,
      "organisation",
      itemTypeIds.LOCATION_ITEM_TYPE_ID,
      "issue",
      "source",
      "party",
      itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
      "directExamination",
      "crossExamination",
      "testimony",
      itemTypeIds.MINDMAP_NODE_TYPE_ID
    ]
  },
  properties: {
    byId: {
      batesNoEnd: {
        enabled: true,
        canDelete: true,
        id: "batesNoEnd",
        key: "batesNoEnd",
        label: "Bates # End",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      notes: {
        enabled: true,
        canDelete: true,
        id: "notes",
        key: "notes",
        label: "Notes",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      legal: {
        enabled: true,
        canDelete: true,
        id: "legal",
        key: "legal",
        label: "Legal",
        format: "boolean",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      gender: {
        enabled: true,
        canDelete: true,
        id: "gender",
        key: "gender",
        label: "Gender",
        format: "list",
        allowed: arrayToListObjectCamelCase(["Male", "Female", "Other"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      depositionExNo: {
        enabled: true,
        canDelete: true,
        id: "depositionExNo",
        key: "depositionExNo",
        label: "Deposition Ex #",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      juryInstruction: {
        enabled: true,
        canDelete: true,
        id: "juryInstruction",
        key: "juryInstruction",
        label: "Jury Instruction",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      relevance: {
        enabled: true,
        canDelete: true,
        id: "relevance",
        key: "relevance",
        label: "Relevance",
        format: "list",
        allowed: arrayToListObjectCamelCase(["High", "Medium", "Low"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      batesNoBegin: {
        enabled: true,
        canDelete: true,
        id: "batesNoBegin",
        key: "batesNoBegin",
        label: "Bates # Begin",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      address: {
        enabled: true,
        canDelete: true,
        id: "address",
        key: "address",
        label: "Address",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      trailExNo: {
        enabled: true,
        canDelete: true,
        id: "trailExNo",
        key: "trailExNo",
        label: "Trial Ex #",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      citation: {
        enabled: true,
        canDelete: true,
        id: "citation",
        key: "citation",
        label: "Citation",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      sourceType: {
        enabled: true,
        canDelete: true,
        id: "sourceType",
        key: "sourceType",
        label: "Source Type",
        format: "list",
        allowed: arrayToListObjectCamelCase([
          "Affadavit",
          "Statement",
          "Contract",
          "Agreement",
          "Letter",
          "Writ",
          "Amendment",
          "Record",
          "Transcript",
          "Research",
          "Communication",
          "Declaration",
          "Permit",
          "Pleading",
          "Other"
        ]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      email: {
        enabled: true,
        canDelete: true,
        id: "email",
        key: "email",
        label: "Email",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      phone: {
        enabled: true,
        canDelete: true,
        id: "phone",
        key: "phone",
        label: "Phone",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      author: {
        enabled: true,
        canDelete: true,
        id: "author",
        key: "author",
        label: "Author",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      contact: {
        enabled: true,
        canDelete: true,
        id: "contact",
        key: "contact",
        label: "Contact",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      status: {
        enabled: true,
        canDelete: true,
        id: "status",
        key: "status",
        label: "Status",
        format: "list",
        allowed: arrayToListObjectCamelCase(["Disputed", "Undisputed", "Prospective"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      shortName: {
        enabled: true,
        canDelete: true,
        id: "shortName",
        key: "shortName",
        label: "Short Name",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      title: {
        enabled: true,
        canDelete: true,
        id: "title",
        key: "title",
        label: "Title",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      },
      roleInCase: {
        enabled: true,
        canDelete: true,
        id: "roleInCase",
        key: "roleInCase",
        label: "Role in Case",
        format: "list",
        allowed: arrayToListObjectCamelCase([
          "Witness",
          "Attorney",
          "Judge",
          "Defendant",
          "Prosecutor",
          "Plaintiff",
          "Barrister",
          "Solicitor",
          "Lawyer",
          "Magistrate",
          "Respondent",
          "Informant",
          "Other"
        ]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null
      }
    },
    allIds: [
      "notes",
      "title",
      "shortName",
      "email",
      "phone",
      "address",
      "gender",
      "roleInCase",
      "sourceType",
      "batesNoBegin",
      "batesNoEnd",
      "depositionExNo",
      "trailExNo",
      "status",
      "citation",
      "author",
      "relevance",
      "contact",
      "legal",
      "juryInstruction"
    ]
  },
  references: {
    byId: {
      participantRef: {
        enabled: true,
        canDelete: true,
        id: "participantRef",
        key: "participantRef",
        label: "Participant",
        types: ["party", itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "midBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      serviceProviderRef: {
        enabled: true,
        canDelete: true,
        id: "serviceProviderRef",
        key: "serviceProviderRef",
        label: "Service provider",
        types: ["organisation"],
        icon: "circle",
        color: "lime500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Customer",
        showInverse: true,
        showInInspector: false
      },
      authorRef: {
        enabled: true,
        canDelete: true,
        id: "authorRef",
        key: "authorRef",
        label: "Author",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID, "organisation"],
        icon: "circle",
        color: "darkPurple",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      producedByRef: {
        enabled: true,
        canDelete: true,
        id: "producedByRef",
        key: "producedByRef",
        label: "Produced By",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID, "organisation"],
        icon: "circle",
        color: "denim",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      counselRef: {
        enabled: true,
        canDelete: true,
        id: "counselRef",
        key: "counselRef",
        label: "Counsel",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID, "organisation"],
        icon: "circle",
        color: "teal500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Client",
        showInverse: true,
        showInInspector: false
      },
      witnessRef: {
        enabled: true,
        canDelete: true,
        id: "witnessRef",
        key: "witnessRef",
        label: "Observer",
        types: ["party", itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "darkBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      contactRef: {
        enabled: true,
        canDelete: true,
        id: "contactRef",
        key: "contactRef",
        label: "Contact",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID, "organisation"],
        icon: "circle",
        color: "lightPink",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      keyFactRef: {
        enabled: true,
        canDelete: true,
        id: "keyFactRef",
        key: "keyFactRef",
        label: "Key Fact",
        types: ["fact", itemTypeIds.EVENT_ITEM_TYPE_ID],
        icon: "circle",
        color: "purple200A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      subsidiaryRef: {
        enabled: true,
        canDelete: true,
        id: "subsidiaryRef",
        key: "subsidiaryRef",
        label: "Subsidiary",
        types: ["organisation"],
        icon: "circle",
        color: "blue200",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Parent",
        showInverse: true,
        showInInspector: false
      },
      employerRef: {
        enabled: true,
        canDelete: true,
        id: "employerRef",
        key: "employerRef",
        label: "Employer",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID, "organisation"],
        icon: "circle",
        color: "deepOrange300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Employee",
        showInverse: true,
        showInInspector: false
      },
      familyRef: {
        enabled: true,
        canDelete: true,
        id: "familyRef",
        key: "familyRef",
        label: "Family",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "pastelPink",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      formerEmployerRef: {
        enabled: true,
        canDelete: true,
        id: "formerEmployerRef",
        key: "formerEmployerRef",
        label: "Former employer",
        types: ["organisation", itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "deepOrange500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Former employee",
        showInverse: true,
        showInInspector: false
      },
      vendorRef: {
        enabled: true,
        canDelete: true,
        id: "vendorRef",
        key: "vendorRef",
        label: "Vendor",
        types: ["organisation"],
        icon: "circle",
        color: "green700A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Buyer",
        showInverse: true,
        showInInspector: false
      },
      mentionsRef: {
        enabled: true,
        canDelete: true,
        id: "mentionsRef",
        key: "mentionsRef",
        label: "Mentions",
        types: [
          itemTypeIds.PERSON_ITEM_TYPE_ID,
          itemTypeIds.LOCATION_ITEM_TYPE_ID,
          "organisation",
          "party",
          "issue",
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "fact"
        ],
        icon: "circle",
        color: "indigo300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Mentioned by",
        showInverse: false,
        showInInspector: false
      },
      associateRef: {
        enabled: true,
        canDelete: true,
        id: "associateRef",
        key: "associateRef",
        label: "Associate",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "indigo400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      friendRef: {
        enabled: true,
        canDelete: true,
        id: "friendRef",
        key: "friendRef",
        label: "Friend",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "fuschia",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      sentToRef: {
        enabled: true,
        canDelete: true,
        id: "sentToRef",
        key: "sentToRef",
        label: "Sent To",
        types: ["organisation", itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "pink200",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      shareholderRef: {
        enabled: true,
        canDelete: true,
        id: "shareholderRef",
        key: "shareholderRef",
        label: "Shareholder",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID, "organisation"],
        icon: "circle",
        color: "yellow700",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      documentRef: {
        enabled: true,
        canDelete: true,
        id: "documentRef",
        key: "documentRef",
        label: "Document",
        types: ["source"],
        icon: "circle filled",
        color: "yellow700",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      factRef: {
        enabled: true,
        canDelete: true,
        id: "factRef",
        key: "factRef",
        label: "Fact",
        types: [itemTypeIds.EVENT_ITEM_TYPE_ID, "fact"],
        icon: "circle",
        color: "purple400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      evidenceRef: {
        enabled: true,
        canDelete: true,
        id: "evidenceRef",
        key: "evidenceRef",
        label: "Evidence",
        types: ["source"],
        icon: "circle",
        color: "lime500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Evidence of",
        showInverse: true,
        showInInspector: false
      },
      sentFromRef: {
        enabled: true,
        canDelete: true,
        id: "sentFromRef",
        key: "sentFromRef",
        label: "Sent From",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID, "organisation"],
        icon: "circle",
        color: "pink300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      locationRef: {
        enabled: true,
        canDelete: true,
        id: "locationRef",
        key: "locationRef",
        label: "Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "orange",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      [REFERENCE_RELATES_TO]: {
        enabled: true,
        canDelete: false,
        id: REFERENCE_RELATES_TO,
        key: REFERENCE_RELATES_TO,
        label: "Relates to",
        types: [
          itemTypeIds.MINDMAP_NODE_TYPE_ID,
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "fact",
          "party",
          itemTypeIds.PERSON_ITEM_TYPE_ID,
          itemTypeIds.LOCATION_ITEM_TYPE_ID,
          "issue",
          "source",
          "organisation",
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
          "testimony",
          "directExamination",
          "crossExamination"
        ],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      investorRef: {
        enabled: true,
        canDelete: true,
        id: "investorRef",
        key: "investorRef",
        label: "Investor",
        types: ["organisation", itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "green300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      coworkerRef: {
        enabled: true,
        canDelete: true,
        id: "coworkerRef",
        key: "coworkerRef",
        label: "Coworker",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "blue200A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      }
    },
    allIds: [
      "participantRef",
      "witnessRef",
      "locationRef",
      "factRef",
      "keyFactRef",
      "evidenceRef",
      "documentRef",
      "familyRef",
      "friendRef",
      "associateRef",
      "coworkerRef",
      "counselRef",
      "employerRef",
      "formerEmployerRef",
      "shareholderRef",
      "investorRef",
      "mentionsRef",
      "producedByRef",
      "authorRef",
      "contactRef",
      "sentFromRef",
      "sentToRef",
      "vendorRef",
      "serviceProviderRef",
      "subsidiaryRef",
      REFERENCE_RELATES_TO
    ]
  },
  calendar,
  colors: {
    byId: {
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC": {
        id: "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
        label: "Light Purple",
        swatchColor: "lightPurple"
      },
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5": {
        id: "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
        label: "Light Teal",
        swatchColor: "lightTeal"
      },
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4": {
        id: "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
        label: "Denim",
        swatchColor: "denim"
      },
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187": {
        id: "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
        label: "Yellow",
        swatchColor: "yellow"
      },
      "199ABF77-578A-4EA8-8C2D-8EA080610A78": {
        id: "199ABF77-578A-4EA8-8C2D-8EA080610A78",
        label: "Dark Purple",
        swatchColor: "darkPurple"
      },
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49": {
        id: "D9DAECFF-F3CE-4399-9690-5E045E8BFA49",
        label: "Light Blue",
        swatchColor: "lightBlue"
      },
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7": {
        id: "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
        label: "Apple",
        swatchColor: "apple"
      },
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7": {
        id: "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
        label: "Red",
        swatchColor: "red"
      },
      "7F4245A2-AF3B-4633-BEBA-68494486C574": {
        id: "7F4245A2-AF3B-4633-BEBA-68494486C574",
        label: "Peach",
        swatchColor: "peach"
      }
    },
    allIds: [
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
      "199ABF77-578A-4EA8-8C2D-8EA080610A78",
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
      "7F4245A2-AF3B-4633-BEBA-68494486C574",
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49"
    ]
  },
  timelineViewSettings: {
    scrollBounds: "infinite",
    eventLayout: "clearAll",
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    showDependencies: true,
    zoomFrom: TimelineZoomLimits.Decade,
    zoomTo: TimelineZoomLimits.Day,
    showTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "fact"]
  },
  narrativeViewSettings: {
    showIdentifier: false,
    showDates: false,
    showDuration: false,
    showNarrative: true,
    showRelatedAvatars: true,
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    columnLevel: 0
  },
  narrativeSettings: {
    labels: defaultLegalNarrativeLabels,
    autoDeleteWhenRemoveContainer: true,
    numbering: narrativeNumberingOptions.OUTLINE_STYLE
  },
  mindmapViewSettings: {
    showIdentifier: true,
    showDates: false,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: true,
    showInfoIcons: false
  },
  subwayViewSettings: {
    showDates: false,
    display: subwayDisplayOptions.SUBWAY
  },
  relationshipViewSettings: {
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showSummary: false,
    showInfoIcons: false
  },
  moveChildItemsWithParent: false
};
